import React, { useState } from "react";

import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import Link from "next/link";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";

import TermsAndConditionsModal from "@/components/TermsAndConditionsModal";
import PrivacyAndPolicyModal from "@/components/PrivacyAndPolicyModal";

import { AuthServices } from "@/services/auth.services";

import { handlingErrorMessage } from "@/utils/handlingErrorMessage";
import { OnboardingServices } from "@/services/onboarding.services";

const RecruiterRegister = () => {
  const router = useRouter();

  const [termsModal, setTermsModal] = useState(false);
  const [privacyModal, setPrivacyModal] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      termAndConditions: false,
      privacyAndPolicy: false,
    },
  });

  const { data: commonData } = useQuery(
    ["commonData"],
    OnboardingServices.getCommonData
  );

  const getFormErrorMessage = (name: string) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const { mutate, isLoading, error } = useMutation(
    (formData: { email: string; password: string }) =>
      AuthServices.registerWithEmailAndPassword(
        formData.email,
        formData.password,
        "recruiter"
      ),
    {
      onSuccess: () => {
        router.push("verification-email");
      },
    }
  );

  const isPersonalEmail = (email: string) => {
    const providers = commonData?.emails?.map(
      (domain: string) => domain?.split(".")[0]
    );

    const regexPattern = `^[a-zA-Z0-9._%+-]+@(${providers?.join(
      "|"
    )})\\.[a-zA-Z]{2,}$`;

    const emailRegex = new RegExp(regexPattern);

    return emailRegex.test(email);
  };

  const onSubmit = async (formData: any) => {
    mutate(formData);
  };

  return (
    <>
      <div className="surface-card border-round w-full">
        <div className="text-center">
          <div className="text-900 text-3xl font-medium mb-3 text-primary">
            Create an account
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-3">
            <label
              htmlFor="email"
              className="block text-900 font-medium mb-2 text-primary"
            >
              Email <span className="text-red-500">*</span>
            </label>

            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "Invalid email address",
                },
                validate: (value) => {
                  if (isPersonalEmail(value)) {
                    return "Enter a valid company email";
                  }
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  id="email"
                  type="text"
                  className={`w-full ${errors.email?.message && "p-invalid"}`}
                />
              )}
            />
            {getFormErrorMessage("email")}
          </div>
          <div className="mb-3">
            <label
              htmlFor="password"
              className="block text-900 font-medium mb-2 text-primary"
            >
              Password <span className="text-red-500">*</span>
            </label>
            <Controller
              name="password"
              control={control}
              rules={{
                required: "Password is required.",
                maxLength: 30,
                minLength: 6,
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  id="password"
                  type="password"
                  className={`w-full ${errors.email?.message && "p-invalid"}`}
                />
              )}
            />
            {getFormErrorMessage("password")}
          </div>
          <div className="mb-3">
            <label
              htmlFor="Confirmpassword"
              className="block text-900 font-medium mb-2 text-primary"
            >
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: "Confirm password is required.",
                validate: (value: string) => {
                  if (watch("password") != value) {
                    return "Your passwords don't match";
                  }
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  id="confirmPassword"
                  type="password"
                  className={`w-full ${errors.email?.message && "p-invalid"}`}
                />
              )}
            />
            {getFormErrorMessage("confirmPassword")}
          </div>

          <div className="flex flex-column align-items-start justify-content-between mb-2">
            <div className="flex align-items-center mb-2">
              <Controller
                name="termAndConditions"
                control={control}
                rules={{
                  required: "Please accept Terms and Conditions.",
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Checkbox
                      inputId={field.name}
                      checked={field.value}
                      inputRef={field.ref}
                      className={`mr-2 ${fieldState.error && "p-invalid"}`}
                      onChange={(e) => field.onChange(e.checked)}
                    />
                    <label
                      className={`text-primary block ${
                        errors.email?.message && "p-invalid"
                      }`}
                      // htmlFor="termAndConditions"
                    >
                      Accept{" "}
                      <span
                        onClick={() => setTermsModal(true)}
                        className="cursor-pointer text-blue-500 text-900"
                      >
                        Terms and Conditions
                      </span>
                      <span className="text-red-500">*</span>
                    </label>
                  </>
                )}
              />
            </div>
            {getFormErrorMessage("termAndConditions")}
          </div>
          <div className="flex flex-column align-items-start justify-content-between mb-6">
            <div className="flex align-items-center mb-2">
              <Controller
                name="privacyAndPolicy"
                control={control}
                rules={{
                  required: "Please accept Privacy Policy.",
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Checkbox
                      inputId={field.name}
                      checked={field.value}
                      inputRef={field.ref}
                      className={`mr-2 ${fieldState.error && "p-invalid"}`}
                      onChange={(e) => field.onChange(e.checked)}
                    />
                    <label
                      className={`text-primary block ${
                        errors.privacyAndPolicy?.message && "p-invalid"
                      }`}
                      // htmlFor="termAndConditions"
                    >
                      Accept{" "}
                      <span
                        onClick={() => setPrivacyModal(true)}
                        className="cursor-pointer text-blue-500 text-900"
                      >
                        Privacy Policy
                      </span>
                      <span className="text-red-500">*</span>
                    </label>
                  </>
                )}
              />
            </div>
            {getFormErrorMessage("privacyAndPolicy")}
          </div>

          <Button label="Sign Up" className="w-full" loading={isLoading} />
        </form>
        <p className="p-invalid mt-2 text-center text-red-500">
          {!!error?.code && handlingErrorMessage(error?.code)}
        </p>
        <div className="text-center mt-5">
          <span className="text-600 font-medium line-height-3 text-primary-500">
            Already have an account?
          </span>
          <Link
            href={`/auth/login/${router.query.trial ? "?trial=true" : ""}`}
            className="font-medium no-underline ml-2 text-blue-500 cursor-pointer text-orange-300"
          >
            Sign in!
          </Link>
        </div>
      </div>
      <TermsAndConditionsModal
        onHide={() => setTermsModal(false)}
        visible={termsModal}
      />
      <PrivacyAndPolicyModal
        onHide={() => setPrivacyModal(false)}
        visible={privacyModal}
      />
    </>
  );
};

export default RecruiterRegister;
